import { Injectable } from '@angular/core';
import { CalculationService } from '../../calculation.service';
import { ConfigurationService } from '../../configuration/configuration.service';
import { CalculationStorageService } from '../../../../../shared/services/calculation-storage.service';
import { OwnerDataConsts } from '../../../../consts/owner-data.consts';
import { OwnerDataModel } from '../../../../models/owner-data/owner-data.model';
import { Dictionary } from '../../../../configuration/dictionary';
import { OwnerDataInterface } from '../../../api/sections-api/owner-data-interface';
import {CoOwnerModel, CoOwnerState} from '../../../../models/owner-data/co-owner.model';
import { DictionaryItem } from '../../../../configuration/dictionary-item';
import { InsurerDataFormStateService } from '../insurer-data/insurer-data-form-state.service';
import { PeselService } from '../../../../../shared/services/pesel.service';
import * as _ from 'lodash';
import { Dictionaries, UiConfig } from '../../../../configuration/ui-config';
import { ApplicationStateService } from '../../../../../shared/services/application-state.service';
import { SalesChannel } from '../../../../consts/sales-channel.enum';
import { AgreementModel } from '../../../../models/agreement.model';
import { AdditionalDriverModel } from '../../../../models/owner-data/additional-driver.model';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {RestApiService} from '../../../../../shared/services/rest-api.service';
import {InitialDataSectionFieldKey} from '../../../../consts/initial-data-section-field-key.enum';
import {FieldsVisibilityService} from '../../../fields-visibility.service';
import {isYearInLastNYears} from '../../../../../shared/utils/time-utils';

@Injectable({
  providedIn: 'root'
})
export class OwnerDataStateService extends OwnerDataConsts implements OwnerDataInterface {

  public ownerDataSection: OwnerDataInterface;
  public oldestYoungChildYearOfBirthDictionary: number[];
  public readonly DRIVING_LICENSE_ISSUE_YEAR_START_YEAR = 1992;

  private dictionaries: Dictionaries;
  private readonly OLDEST_YOUNG_CHILD_MAX_AGE = 25;
  private readonly DRIVING_LICENSE_ISSUE_MONTH_COLLECTED_YEARS_BACK = 3;

  constructor(private logicService: CalculationService,
              private stateService: ApplicationStateService,
              private insurer: InsurerDataFormStateService,
              private configuration: ConfigurationService,
              private api: RestApiService,
              private storage: CalculationStorageService,
              private fieldsVisibilityService: FieldsVisibilityService) {
    super();
    this.dictionaries = this.configuration.uiConfig.dictionaries;
    this.initOldestYoungChildYearOfBirthDictionary();
  }

  get ownerData(): OwnerDataModel {
    return this.logicService.ownerData;
  }

  get ownerContactData(): any {
    return this.logicService.contactData;
  }
  get onwerAgreements(): any {
    return this.logicService.agreements;
  }

  get additionalDrivers(): AdditionalDriverModel[] {
    return this.logicService.additionalDrivers;
  }

  get showCompanyPartyTypeCode(): boolean {
    return false;
  }

  get drivingLicenseIssueYearDictionary(): Dictionary {
    if (this.dictionaries) {
      return this.dictionaries[UiConfig.DRIVING_LICENSE_ISSUE_YEAR];
    }
    return null;
  }

  get professionsDictionary(): Dictionary {
    if (this.dictionaries) {
      this.dictionaries[UiConfig.PROFESSIONS].items.sort(this.sortProfessionsByValue);
      return this.dictionaries[UiConfig.PROFESSIONS];
    }
    return null;
  }

  get matrimonialStatusDictionary(): any {
    if (this.dictionaries) {
      return this.dictionaries[UiConfig.MATRIMONIAL_STATUS];
    }
    return null;
  }

  get partyTypeCode(): string {
    return this.ownerData.partyTypeCode;
  }

  get coOwnersOption(): number {
    if (this.ownerData.coOwners) {
      return this.ownerData.coOwners.length;
    }
    return 0;
  }

  get additionalDriversOption(): number {
    return this.additionalDrivers ? this.additionalDrivers.length : 0;
  }

  get agreements(): AgreementModel[] {
    return this.logicService.agreements;
  }

  set agreements(agreements: AgreementModel[]) {
    this.logicService.agreements = agreements;
  }

  get noDrivingLicense(): boolean {
    return this.stateService.state.noDrivingLicense;
  }

  get coOwnersState(): CoOwnerState[] {
    return this.stateService.state.calculationState.coOwnersState;
  }

  get birthDateVisible(): boolean {
    return this.fieldsVisibilityService.isFieldVisible('INITIAL_DATA_SECTION', InitialDataSectionFieldKey.BIRTH_DATE);
  }

  get drivingLicenseIssueYearVisible(): boolean {
    return this.fieldsVisibilityService.isFieldVisible('OWNER_DATA_SECTION', OwnerDataConsts.DRIVING_LICENSE_ISSUE_YEAR);
  }

  get drivingLicenseIssueMonthVisible(): boolean {
    return this.fieldsVisibilityService.isFieldVisible('OWNER_DATA_SECTION', OwnerDataConsts.DRIVING_LICENSE_ISSUE_MONTH);
  }

  changeAgreement(code: string, selected: boolean): void {
    this.getAgreement(code).selectedAnswer = this.getSelectedAgreementAnswer(code, selected);
    this.storage.updateCalculation();
  }

  public updateCoOwners(coOwners: CoOwnerModel[]): void {
    this.ownerData.coOwners = coOwners;
    this.updateCalculationData();
  }

  public updateAdditionalDrivers(additionalDrivers: AdditionalDriverModel[]): void {
    this.logicService.additionalDrivers = additionalDrivers;
    this.updateCalculationData();
  }

  public updateCalculationData(): void {
    this.storage.updateCalculation();
  }

  public changeNIP(nip: string): void {
    this.ownerData.nip = nip;
    this.insurer.changeNIP(nip);
    this.storage.updateCalculation();
  }

  public changePesel(pesel: string): void {
    pesel = isNotNullOrUndefined(pesel) && pesel.length > 0 ? pesel : null;
    this.insurer.changePesel(pesel);
    this.ownerData.pesel = pesel;
    if (!this.birthDateVisible) {
      this.ownerData.birthDate = PeselService.getBirthdate(pesel);
    }
  }

  public changeLastName(lastName: string): void {
    lastName = lastName.length ? lastName : null;
    this.ownerData.lastName = lastName;
    this.insurer.changeLastName(lastName);
    this.storage.updateCalculation();
  }

  public changeFirstName(firstName: string): void {
    firstName = firstName.length ? firstName : null;
    this.ownerData.firstName = firstName;
    this.insurer.changeFirstName(firstName);
    this.storage.updateCalculation();
  }

  public changeCompanyName(companyName: string): void {
    this.ownerData.companyName = companyName;
    this.insurer.changeCompanyName(companyName);
    this.storage.updateCalculation();
  }

  public changeBirthDate(birthDate: Date): void {
    this.ownerData.birthDate = birthDate;
    this.storage.updateCalculation();
  }

  public changePartyTypeCode(partyTypeCode: string): void {
    this.ownerData.partyTypeCode = partyTypeCode;
    this.insurer.changePartyTypeCode(partyTypeCode);
    this.storage.updateCalculation();
  }

  public changePostalCode(postalCode: string): void {
    this.ownerData.address.postalCode = postalCode;
    this.insurer.changePostalCode(postalCode);
    this.storage.updateCalculation();
  }

  public changeDrivingLicenseIssueYear(drivingLicenseIssueYear: number): void {
    this.ownerData.drivingLicenseIssueYear = drivingLicenseIssueYear;
    this.insurer.changeDrivingLicenseIssueYear(drivingLicenseIssueYear);
    this.storage.updateCalculation();
  }

  public changeNoDrivingLicense(noDrivingLicense: boolean): void {
    this.stateService.state.noDrivingLicense = noDrivingLicense;
    this.stateService.updateApplicationState();
  }

  public changeDrivingLicenseIssueMonth(drivingLicenseIssueMonth: number): void {
    this.ownerData.drivingLicenseIssueMonth = drivingLicenseIssueMonth;
    this.storage.updateCalculation();
  }

  public changeMatrimonialStatus(status: string): void {
    this.ownerData.matrimonialStatus = status;
    this.storage.updateCalculation();
  }

  public changeChildrenUnder26(childrenUnder26: boolean): void {
    this.ownerData.childrenUnder26 = childrenUnder26;
    if (!childrenUnder26) {
      delete this.ownerData.oldestYoungChildYearOfBirth;
    }
    this.storage.updateCalculation();
  }

  public changeOldestYoungChildYearOfBirth(age: number): void {
    this.ownerData.oldestYoungChildYearOfBirth = age;
    this.storage.updateCalculation();
  }

  changeEmail(email: string): void {
    this.ownerContactData.email = email;
    this.storage.updateCalculation();
  }

  changePhoneNumber(phoneNumber: string): void {
    this.ownerContactData.phoneNumber = phoneNumber;
    this.storage.updateCalculation();
  }

  addCoOwnerToState(): void {
    this.stateService.state.calculationState.coOwnersState.push({
      noDrivingLicense: false
    });
    this.stateService.updateApplicationState();
  }

  clearCoOwnersState(): void {
    this.stateService.state.calculationState.coOwnersState = [];
    this.stateService.updateApplicationState();
  }

  changeCoOwnerNoDrivingLicense(coOwnerIndex: number, noLicense: boolean): void {
    this.stateService.state.calculationState.coOwnersState[coOwnerIndex].noDrivingLicense = noLicense;
    this.stateService.updateApplicationState();
  }

  getCoOwnerNoDrivingLicense(coOwnerIndex: number): boolean {
    if (this.stateService.state.calculationState.coOwnersState[coOwnerIndex]) {
      return this.stateService.state.calculationState.coOwnersState[coOwnerIndex].noDrivingLicense;
    }
    return false;
  }

  async validatePostalCode(postalCode: string) {
    const url = this.configuration.uiConfig.postalCodeValidationEndpoint;
    return this.api.get(`${url}?postalCode=${postalCode}`, {}, true);
  }

  public arePeselNumbersUnique(): boolean {
    let pesels = [];
    pesels.push(this.ownerData.pesel);
    this.ownerData.coOwners.forEach(coOwner => {
      pesels.push(coOwner.pesel);
    });
    pesels = pesels.filter(pesel => isNotNullOrUndefined(pesel));
    const uniquePesels = _.uniq(pesels);
    return pesels.length === uniquePesels.length;
  }

  shouldCollectDrivingLicenseIssueMonth(issueYear: number): boolean {
    return isYearInLastNYears(issueYear, this.DRIVING_LICENSE_ISSUE_MONTH_COLLECTED_YEARS_BACK);
  }

  private sortProfessionsByValue({ value: firstValue }: DictionaryItem, { value: secondValue }: DictionaryItem): number {
    return +firstValue - +secondValue;
  }

  private getAgreement(code: string): AgreementModel {
    return this.logicService.agreements.filter(item => item.code === code)[0];
  }

  private getSelectedAgreementAnswer(code: string, selected: boolean): string {
    const answers = this.getAgreement(code).answers;
    const suffix = selected ? 'YES' : 'NO';
    return answers.find(answer => answer.code.endsWith(suffix)).code;
  }

  private initOldestYoungChildYearOfBirthDictionary(): void {
    const currentYear = (new Date()).getFullYear();
    const minYear = currentYear - this.OLDEST_YOUNG_CHILD_MAX_AGE - 1;
    this.oldestYoungChildYearOfBirthDictionary = _.range(currentYear, minYear - 1);
  }
}
